import React, { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import 'react-toastify/dist/ReactToastify.css';
import { validateSchema } from '../validators';
import CardRenderer from './CardRenderer';
import { DataReloadProvider } from './DataReloadContext';
import ErrorBoundary from './ErrorBoundary';
import './PageRenderer.css';
import TabRenderer from './TabRenderer';

interface Tab {
  id?: string;
  name?: string;
  card_list?: any[];
  cards_layout?: {
    layout?: string;
    columns?: number;
  };
}

interface Card {
  [key: string]: any;
}

interface Schema {
  aiChat?: boolean;
  header?: {
    logo_url?: string;
    title?: string;
    button?: {
      label?: string;
      variant?: string;
      modal_display?: any;
    };
  };
  tabs?: Tab[];
  card_list?: Card[];
  cards?: Card[];
  cards_layout?: {
    layout?: string;
    columns?: number;
  };
}

interface PageRendererProps {
  schema: Schema;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  updatedTabs?: Set<string>;
  clearTabUpdateIndicator?: (tabId: string) => void;
}

const PageRenderer: React.FC<PageRendererProps> = ({
  schema,
  activeTab,
  setActiveTab,
  updatedTabs,
  clearTabUpdateIndicator,
}) => {
  // Initialize state from localStorage, default to true if not set
  const [isChatOpen, setIsChatOpen] = useState(() => {
    const stored = localStorage.getItem('chatPanelOpen');
    return stored === null ? true : stored === 'true';
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Update localStorage when chat panel state changes
  useEffect(() => {
    localStorage.setItem('chatPanelOpen', isChatOpen.toString());
  }, [isChatOpen]);

  // Validate schema first
  const schemaValidation = validateSchema(schema);
  if (!schemaValidation.isValid) {
    console.warn('Schema validation failed:', schemaValidation.errors);
    return (
      <DataReloadProvider>
        <div className="container-fluid mt-4">
          <Alert variant="danger">
            <Alert.Heading>⚠️ Invalid Page Schema</Alert.Heading>
            <p className="mb-2 text-muted small">
              <strong>ept AI backend error.</strong> Please send an email to{' '}
              <a href="mailto:support@ept.ai">support@ept.ai</a> with a
              screenshot or the text below.
            </p>
            <p className="mb-1">
              This page has invalid schema data and cannot be displayed:
            </p>
            <ul className="mb-0">
              {schemaValidation.errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        </div>
      </DataReloadProvider>
    );
  }

  // Calculate className once
  const mainContentClassName = `main-content${schema?.aiChat === true && isChatOpen ? ' chat-open' : ''}`;

  return (
    <DataReloadProvider>
      <div>
        {/* Header Section */}
        {/* {schema?.header && (
                    <header className="page-header">
                        <div className='page-header-left'>
                        {schema?.header?.logo_url && (
                            <img className="page-header-logo" src={schema?.header?.logo_url} alt="logo" />
                        )}
                        <h1>{schema?.header?.title}</h1>
                        </div>
                        <div className='page-header-right'>
                        {schema?.header?.button && (
                            <button
                                className={`btn btn-${schema?.header?.button?.variant}`}
                                onClick={() => setIsModalOpen(true)}
                            >
                                {schema?.header?.button?.label}
                            </button>
                        )}
                        </div>
                    </header>
                )} */}

        {/* Modal */}
        {/* {schema?.header?.button?.modal_display && (
                    <Modal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        title={schema.header.button.modal_display.modal_title}
                        content={
                            <ModalForm
                                title={schema.header.button.modal_display.content.title}
                                fields={schema.header.button.modal_display.content.fields}
                                submitAction={schema.header.button.modal_display.content.submit_action}
                                onClose={() => setIsModalOpen(false)}
                            />
                        }
                        size={schema.header.button.modal_display.size}
                        hasCloseButton={schema.header.button.modal_display.has_close_button}
                        hasBackdrop={schema.header.button.modal_display.has_backdrop}
                    />
                )} */}

        {/* Content Section */}
        {schema.tabs && schema.tabs.length > 0 ? (
          // Render tabs if they exist
          <div className="tabs-container">
            <TabRenderer
              tabs={schema.tabs}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              schema={schema}
              updatedTabs={updatedTabs}
              clearTabUpdateIndicator={clearTabUpdateIndicator}
            />
          </div>
        ) : (
          <div
            className={`cards-container ${schema.cards_layout?.layout || 'column'}`}
          >
            <div
              className={`cards-grid ${schema.cards_layout?.layout === 'row' ? 'row-layout' : 'column-layout'}`}
              style={{
                gridTemplateColumns:
                  schema.cards_layout?.layout === 'row'
                    ? `repeat(${schema.cards_layout?.columns || 1}, 1fr)`
                    : '1fr',
              }}
            >
              {schema.card_list &&
                schema.card_list.map((card, index) => (
                  <ErrorBoundary
                    key={index}
                    fallbackType="card"
                    title="Card Error"
                  >
                    <CardRenderer card={card} />
                  </ErrorBoundary>
                ))}
              {schema.cards &&
                schema.cards.map((card, index) => (
                  <ErrorBoundary
                    key={index}
                    fallbackType="card"
                    title="Card Error"
                  >
                    <CardRenderer card={card} />
                  </ErrorBoundary>
                ))}
            </div>
          </div>
        )}
      </div>
    </DataReloadProvider>
  );
};

export default PageRenderer;
